import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer className="bg-white text-slate-700 border-t border-slate-200" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_09913581-f85f-4d5b-af7c-7207d8b9779f/artifacts/akxca88t_Logo_neu.png" 
                alt="Kauz Logistik Logo"
                className="h-20 w-auto"
              />
            </div>
            <p className="text-slate-500 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 uppercase tracking-wider text-slate-900">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {[
                { name: t('footer.home'), path: "/" },
                { name: t('footer.servicesLink'), path: "/leistungen" },
                { name: t('footer.companyLink'), path: "/unternehmen" },
                { name: t('footer.blogLink'), path: "/blog" },
                { name: t('footer.contactLink'), path: "/kontakt" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-500 hover:text-orange-600 transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    <ArrowRight 
                      size={14} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      aria-hidden="true" 
                    />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6 uppercase tracking-wider text-slate-900">{t('footer.servicesTitle')}</h4>
            <ul className="space-y-3">
              {[
                t('services.shipping.title'),
                t('services.express.title'),
                t('services.fulfillment.title'),
                t('services.packaging.title'),
              ].map((service) => (
                <li key={service}>
                  <Link
                    to="/leistungen"
                    className="text-slate-500 hover:text-orange-600 transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight 
                      size={14} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      aria-hidden="true" 
                    />
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6 uppercase tracking-wider text-slate-900">{t('footer.contactTitle')}</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="https://maps.google.com/?q=Industriestr.+18,+63834+Sulzbach"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-500 hover:text-orange-600 transition-colors"
                >
                  <MapPin size={20} className="mt-1 flex-shrink-0" aria-hidden="true" />
                  <span>
                    Kauz Logistik e.K.<br />
                    Industriestr. 18<br />
                    63834 Sulzbach
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:060284062310"
                  className="flex items-center gap-3 text-slate-500 hover:text-orange-600 transition-colors"
                >
                  <Phone size={20} className="flex-shrink-0" aria-hidden="true" />
                  <span>06028 4062310</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:hallo@kauzlogistik.de"
                  className="flex items-center gap-3 text-slate-500 hover:text-orange-600 transition-colors"
                >
                  <Mail size={20} className="flex-shrink-0" aria-hidden="true" />
                  <span>hallo@kauzlogistik.de</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-slate-400 text-sm">
              © {currentYear} Kauz Logistik e.K. {t('footer.copyright')}
            </div>
            <div className="text-slate-400 text-sm">
              {t('footer.ceo')}: Waldemar Kauz
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/kontakt" className="text-slate-400 hover:text-orange-600 transition-colors">
                {t('footer.imprint')}
              </Link>
              <Link to="/kontakt" className="text-slate-400 hover:text-orange-600 transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
